import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Tabs } from 'antd';
import { useDispatch } from 'react-redux';
import { useReactToPrint } from 'react-to-print';
import generatePDF from 'react-to-pdf';
import { getListAction } from 'store/pageSlice';
import { getPodUrl } from 'store/approvalSlice';
import {
	getTripListActionV2,
	getTripMarginStatementV2,
	searchProofOfDeliveryV2,
	getPodUrlsV2,
	getConsignmentNoteV2,
} from 'store/tripSliceV2';
import { getUIVersion } from 'utils/router-static-data';
import {
	TripOverviewTab,
	ExpensesTab,
	PODTab,
	LROverviewTab,
	TemperatureTab,
	DocumentsTab,
} from './components';
import { ACL } from 'components/common';
import { useAuth } from 'hooks';

const { TabPane } = Tabs;

function TripOverviewPage() {
	const { tripId } = useParams();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const { permissions } = useAuth();
	const { user_type } = useAuth();

	// Check UI version to determine which API to use
	const isV2UI = getUIVersion() === 'new';

	// const [searchValue, setSearchValue] = useState('');
	const [activeTab, setActiveTab] = useState('trip-overview');
	const [lrDetails, setLrDetails] = useState(null);
	const [lrLoading, setLrLoading] = useState(false);
	const [lrError, setLrError] = useState(null);
	const [tripDetails, setTripDetails] = useState(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [printVisible, setPrintVisible] = useState(false);
	const [isPDFPrint, setPDFPrint] = useState(false);
	const [selectedLRDetails, setSelectedLRDetails] = useState(null);
	const [expensesData, setExpensesData] = useState([]);
	const [expensesLoading, setExpensesLoading] = useState(false);
	const [podData, setPodData] = useState(null);
	const [podLoading, setPodLoading] = useState(false);
	const [podError, setPodError] = useState(null);
	const [blobUrl, setBlobUrl] = useState(null); // Track blob URLs for cleanup
	const [podKey, setPodKey] = useState(null);
	const [podUploadData, setPodUploadData] = useState(null);
	const LRPrintRef = useRef();
	const printComponent = useReactToPrint({
		content: () => LRPrintRef.current,
		pageStyle: '@page { size: 42cm 56cm }',
	});

	// Helper function to fetch POD data
	// This function first searches for proof of delivery data to get the trip ID,
	// then uses that ID to fetch the POD URL
	// Uses V2 or V1 APIs based on UI version
	const fetchPODData = async (currentTripId) => {
		try {
			const normalizedTripId = currentTripId != null ? String(currentTripId) : '';
			// First, search for proof of delivery data to get the POD ID
			let searchRes;
			if (isV2UI) {
				searchRes = await dispatch(
					searchProofOfDeliveryV2({
						trip_id: normalizedTripId,
						limit: 10,
						offset: 0,
					})
				);
			} else {
				searchRes = await dispatch(
					getListAction({
						search_type: 'proof_of_delivery',
						trip_id: normalizedTripId,
						limit: 10,
						offset: 0,
					})
				);
			}

			setPodUploadData(searchRes?.rows?.[0] || searchRes?.data?.[0]);
			setPodKey(
				searchRes?.rows?.[0]?.proof_of_delivery_key ||
					searchRes?.data?.[0]?.proof_of_delivery_key
			);

			// Find the POD data from the search results
			const foundPodData =
				searchRes?.rows?.find(
					(item) =>
						String(item.trip_id) === normalizedTripId ||
						String(item.id) === normalizedTripId
				) ||
				searchRes?.data?.find(
					(item) =>
						String(item.trip_id) === normalizedTripId ||
						String(item.id) === normalizedTripId
				);

			if (foundPodData && foundPodData.id) {
				console.log('Found POD data with ID:', foundPodData.id);
				// Now use the found ID to get the POD URL
				if (isV2UI) {
					return await dispatch(getPodUrlsV2({ id: foundPodData.id }));
				}
				return await dispatch(getPodUrl({ id: foundPodData.id }));
			}
			// Fallback: try to get POD URL directly with trip ID
			if (isV2UI) {
				return await dispatch(getPodUrlsV2({ id: normalizedTripId }));
			}
			return await dispatch(getPodUrl({ id: normalizedTripId }));
		} catch (fetchError) {
			console.error('Error in fetchPODData:', fetchError);
			throw fetchError;
		}
	};

	// eslint-disable-next-line sonarjs/cognitive-complexity
	useEffect(() => {
		if (tripId) {
			setLoading(true);
			const tripAction = isV2UI
				? getTripListActionV2({
						search_type: 'trip',
						id: tripId,
						limit: 1,
						offset: 0,
				  })
				: getListAction({
						search_type: 'trip',
						id: tripId,
						filterflag: false,
				  });

			dispatch(tripAction)
				.then((res) => {
					const trip = res?.rows?.[0];
					if (trip) {
						const projectDisplayName = isV2UI
							? trip.project_name ||
							  trip.info?.project_name ||
							  trip.project_location_code ||
							  trip.info?.project_location_code ||
							  trip.project_code ||
							  '-'
							: trip.project_location_code || trip.info?.project_location_code || '-';
						const vendorTypeDisplay = isV2UI
							? trip.vendor_type || trip.vendor_details?.vendor_type || '-'
							: trip.vendor_type || '-';
						const driverNumberDisplay = isV2UI
							? trip.info?.driver_number || trip.driver_mobile || '-'
							: trip.info?.driver_number || '-';
						const vehicleTypeDisplay = isV2UI
							? trip.vehicle_type ||
							  trip.vehicle_type_name ||
							  trip.route_vehicle_type_name ||
							  '-'
							: trip.vehicle_type || '-';
						const startKmDisplay =
							trip.start_km ??
							trip.start_odo ??
							trip.info?.odometer_reading_start ??
							'-';
						const endKmDisplay =
							trip.end_km ?? trip.end_odo ?? trip.info?.odometer_reading_end ?? '-';

						// Build reporting date/time string from the best available source
						let reportingDateTime;
						if (isV2UI && trip.reporting_date) {
							// V2: reporting_date is 'DD/MM/YYYY HH:mm:ss', take only the date part.
							// The actual IST time lives in info.reporting_time ('HH:mm:ss').
							const datePart = trip.reporting_date.split(' ')[0]; // 'DD/MM/YYYY'
							const timePart = trip.info?.reporting_time || '00:00:00';
							// Convert DD/MM/YYYY → YYYY-MM-DD so moment can parse reliably
							const [dd, mm, yyyy] = datePart.split('/');
							reportingDateTime = `${yyyy}-${mm}-${dd}T${timePart}`;
						} else if (trip?.info?.reporting_date && trip?.info?.reporting_time) {
							// V1: combine date and time from info object
							reportingDateTime = `${trip.info.reporting_date} ${trip.info.reporting_time}`;
						} else if (trip?.info?.reporting_date) {
							reportingDateTime = trip.info.reporting_date;
						} else {
							reportingDateTime = trip.reporting_time;
						}

						setTripDetails({
							is_vendor_billed: trip.is_vendor_billed,
							client: trip.client_name || '-',
							project: projectDisplayName,
							vendor: trip.vendor_name || trip.info?.vendor_name || '-',
							reportingDateTime,
							startDateTime: (() => {
								if (isV2UI && trip.actual_start_time) {
									// V2: actual_start_time is 'DD/MM/YYYY HH:mm:ss'
									// Convert DD/MM/YYYY → YYYY-MM-DD so moment can parse reliably
									const [datePart, timePart] = trip.actual_start_time.split(' ');
									const [dd, mm, yyyy] = datePart.split('/');
									return `${yyyy}-${mm}-${dd}T${timePart}`;
								}
								return trip.start_time;
							})(),
							endDateTime: trip.end_time,
							runTime: (() => {
								const duration = parseFloat(trip.trip_duration_hours);
								if (Number.isNaN(duration)) return '-';
								const hours = Math.floor(duration);
								const minutes = Math.round((duration - hours) * 60);
								return `${hours} hrs ${minutes} mins`;
							})(),
							sourceLocation: trip.source_location || trip.source_pincode || '-',
							destinationLocation:
								trip.destination_location || trip.destination_pincode || '-',
							tripType: (() => {
								if (trip.trip_type === 'adhoc') return 'Adhoc Trip';
								if (trip.trip_type === 'regular') return 'Regular Trip';
								return trip.trip_type || 'Regular Trip';
							})(),
							invoiceNumber: trip.invoice_number || '-',
							invoiceDate: trip.invoice_date || '-',
							vendorType: vendorTypeDisplay,
							projectName: projectDisplayName,
							city: trip.city || '-',
							clientRate: trip.client_trip_cost || '-',
							vendorRate: trip.vendor_trip_cost || '-',
							driverNumber: driverNumberDisplay,
							driverName: trip.info?.driver_name || trip.driver_name || '-',
							vehicleType: vehicleTypeDisplay,
							vehicleNumber: trip.vehicle_number || '-',
							lrId: trip.id,
							interCity:
								trip.movement_type === 'Intercity' ||
								trip?.info?.is_inter_city === true ||
								false,
							reqProof: trip.info?.reqirement_proof || false,
							endTime: trip.end_time,
							isEnded: !!trip.end_time,
							startKm: startKmDisplay,
							endKm: endKmDisplay,
							mapKm: trip.map_km,
							tripStatus: trip.trip_status_name,
							tripApprovalStatus: trip.trip_approval_status,
							isApproved: trip.is_approved,
							isValid: trip.is_valid,
							podStatus: trip.pod_status,
							createdAt: trip.created_at,
							updatedAt: trip.updated_at,
							info: trip.info,
							trip_status: trip.trip_status,
							trip_documents: trip.trip_documents,
							toll_info: trip.toll_info,
							requirement_proof: trip.info?.requirement_proof,
							is_temperature_enabled: trip?.is_temperature_control,
							adhocRequestId: trip.adhoc_request_id || '-',
							advanceAmount: trip.advance_amount || '-',
							tripStatusName: trip.trip_status_name || '-',
						});
					}
					setLoading(false);
				})
				.catch(() => {
					setError('Failed to fetch trip details');
					setLoading(false);
				});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch, tripId, isV2UI]);

	const isTemperatureEnabled = tripDetails?.is_temperature_enabled;
	useEffect(() => {
		if (activeTab === 'lr-overview' && tripDetails?.interCity && tripId) {
			setLrLoading(true);
			const lrAction = isV2UI
				? getConsignmentNoteV2({ trip_id: tripId })
				: getListAction({
						search_type: 'consignment_note',
						trip_id: tripId,
				  });

			dispatch(lrAction)
				.then((res) => {
					setLrDetails(res?.rows?.[0] || null);
					setLrLoading(false);
				})
				.catch(() => {
					setLrError('Failed to fetch LR details');
					setLrLoading(false);
				});
		}
	}, [activeTab, tripDetails, dispatch, tripId, isV2UI]);

	// Fetch expenses data when expenses tab is active
	// eslint-disable-next-line sonarjs/cognitive-complexity
	useEffect(() => {
		if (activeTab === 'expenses' && tripId) {
			setExpensesLoading(true);
			const expensesAction = isV2UI
				? getTripMarginStatementV2({ trip_id: tripId })
				: getListAction({
						search_type: 'trip_margin_statement',
						trip_id: tripId,
				  });

			dispatch(expensesAction)
				.then((res) => {
					// Try different possible response structures
					// V2 returns { data: [...], rows: [...] }
					// V1 returns { data: [...] } or { rows: [...] }

					const data =
						res?.data || res?.rows || res?.payload?.data || res?.payload?.rows || [];
					console.log('Final data to set:', data);
					setExpensesData(data);
					setExpensesLoading(false);
				})
				// eslint-disable-next-line no-shadow
				.catch((error) => {
					console.error('Expenses API Error:', error);
					setExpensesLoading(false);
				});
		}
	}, [activeTab, tripId, dispatch, isV2UI]);

	// Fetch POD data when POD tab is active
	// eslint-disable-next-line sonarjs/cognitive-complexity
	useEffect(() => {
		if (activeTab === 'pod' && tripId) {
			setPodLoading(true);
			setPodError(null); // Clear any previous errors

			fetchPODData(tripId)
				.then((podUrlRes) => {
					console.log('POD URL API Response:', podUrlRes);
					console.log('POD URL Response structure:', JSON.stringify(podUrlRes, null, 2));

					// Handle different response structures
					// V2 returns: { proof_of_delivery_url, pod_document_url, signature_url, ... }
					// V1 returns: { data: [{ proof_of_delivery_url, ... }] } or { rows: [{ ... }] }
					let data = null;

					// V2 response is already the data object directly
					if (
						isV2UI &&
						podUrlRes &&
						typeof podUrlRes === 'object' &&
						!Array.isArray(podUrlRes)
					) {
						// V2 returns the URL object directly from getPodUrlsV2
						if (podUrlRes.proof_of_delivery_url || podUrlRes.pod_document_url) {
							data = podUrlRes;
						}
					} else if (podUrlRes?.data) {
						// V1 response handling
						data = Array.isArray(podUrlRes.data) ? podUrlRes.data[0] : podUrlRes.data;
					} else if (podUrlRes?.rows) {
						data = Array.isArray(podUrlRes.rows) ? podUrlRes.rows[0] : podUrlRes.rows;
					} else if (podUrlRes?.payload?.data) {
						data = Array.isArray(podUrlRes.payload.data)
							? podUrlRes.payload.data[0]
							: podUrlRes.payload.data;
					} else if (podUrlRes?.payload?.rows) {
						data = Array.isArray(podUrlRes.payload.rows)
							? podUrlRes.payload.rows[0]
							: podUrlRes.payload.rows;
					}

					console.log('Final POD data to set:', data);
					console.log('POD data structure:', JSON.stringify(data, null, 2));

					// Check if the response contains binary data (starts with PNG, JPEG, etc.)
					if (
						data &&
						typeof data === 'string' &&
						(data.startsWith('PNG') ||
							data.startsWith('JPEG') ||
							data.startsWith('GIF'))
					) {
						console.log('Detected binary image data, creating blob URL');
						// Create a blob URL from the binary data
						const blob = new Blob([data], { type: 'image/png' });
						const newBlobUrl = URL.createObjectURL(blob);
						setPodData({ proof_of_delivery_url: newBlobUrl });
						setBlobUrl(newBlobUrl); // Store the blob URL
					} else if (data && (data.proof_of_delivery_url || data.url)) {
						setPodData(data);
					} else {
						setPodError('No POD URL found for this trip');
					}
					setPodLoading(false);
				})
				.catch((podApiError) => {
					console.error('POD API Error:', podApiError);
					setPodError(podApiError.message || 'No pod exists for this trip');
					setPodLoading(false);
				});
		}
	}, [activeTab, tripId, dispatch, isV2UI]);

	// const handleSearch = () => {
	// 	console.log('Searching for:', searchValue);
	// };

	const handleDownload = () => {
		setSelectedLRDetails(lrDetails);
		setPDFPrint(true);
		setPrintVisible(true);
	};

	const handlePrint = () => {
		setSelectedLRDetails(lrDetails);
		setPDFPrint(false);
		setPrintVisible(true);
	};

	useEffect(() => {
		if (printVisible) {
			setTimeout(() => {
				if (isPDFPrint) {
					generatePDF(LRPrintRef, {
						filename: `CSN${String(selectedLRDetails?.id).padStart(6, '0')}.pdf`,
						method: 'save',
						resolution: 1,
						page: { margin: 5, format: 'A4', orientation: 'portrait' },
						canvas: { mimeType: 'image/jpeg', qualityRatio: 1 },
						overrides: {
							pdf: { compress: true },
							canvas: { useCORS: true },
							unit: 'mm',
						},
					});
				} else {
					printComponent();
				}
				setPrintVisible(false);
				setPDFPrint(false);
			}, 200);
		}
	}, [printVisible, isPDFPrint, selectedLRDetails, printComponent]);

	useEffect(
		() => () => {
			if (blobUrl) {
				URL.revokeObjectURL(blobUrl);
			}
		},
		[blobUrl]
	);

	// Permission checks for tab visibility
	const canViewExpenses =
		(user_type !== 'supervisor' && permissions?.search?.includes('trip_margin_statement')) ||
		permissions?.search?.includes('all');
	const canViewPOD =
		permissions?.search?.includes('proof_of_delivery') || permissions?.search?.includes('all');
	const canViewLR =
		permissions?.search?.includes('consignment_note') || permissions?.search?.includes('all');

	return (
		<div style={{ padding: 32, marginTop: '-40px' }}>
			<Tabs activeKey={activeTab} onChange={setActiveTab}>
				<TabPane tab="Trip Overview" key="trip-overview">
					<TripOverviewTab
						tripDetails={tripDetails}
						loading={loading}
						error={error}
						// searchValue={searchValue}
						// setSearchValue={setSearchValue}
						// handleSearch={handleSearch}
						tripId={tripId}
						navigate={navigate}
						isV2={isV2UI}
					/>
				</TabPane>
				{canViewExpenses && tripDetails?.isEnded && (
					<TabPane tab="Expenses" key="expenses">
						<ACL action="search" module="trip_margin_statement">
							<ExpensesTab
								tripDetails={tripDetails}
								tripId={tripId}
								expensesData={expensesData}
								expensesLoading={expensesLoading}
							/>
						</ACL>
					</TabPane>
				)}
				{tripDetails?.isEnded && isTemperatureEnabled === true && (
					<TabPane tab="Temperature" key="temperature">
						<TemperatureTab tripId={tripId} />
					</TabPane>
				)}
				{canViewPOD && tripDetails?.interCity && tripDetails?.isEnded && (
					<TabPane tab="POD" key="pod">
						<ACL action="search" module="proof_of_delivery">
							<PODTab
								podUploadData={podUploadData}
								podData={podData}
								podLoading={podLoading}
								podError={podError}
								podKey={podKey}
								isV2={false}
							/>
						</ACL>
					</TabPane>
				)}
				{canViewLR && tripDetails?.interCity && (
					<TabPane tab="LR Overview" key="lr-overview">
						<ACL action="search" module="consignment_note">
							<LROverviewTab
								lrDetails={lrDetails}
								lrLoading={lrLoading}
								lrError={lrError}
								handleDownload={handleDownload}
								handlePrint={handlePrint}
								tripDetails={tripDetails}
								tripId={tripId}
								navigate={navigate}
								printVisible={printVisible}
								LRPrintRef={LRPrintRef}
								selectedLRDetails={selectedLRDetails}
								isPDFPrint={isPDFPrint}
							/>
						</ACL>
					</TabPane>
				)}
				<TabPane tab="Documents" key="documents">
					<DocumentsTab tripDetails={tripDetails} loading={loading} isV2={false} />
				</TabPane>
			</Tabs>
		</div>
	);
}

export default TripOverviewPage;
